import { decodeFunctionResult, encodeFunctionData, erc20Abi, type Address, type Hex } from "viem";
import { ELYSIUM_TRADE, sameTradeAddress, type TradeConfig } from "../config/trade";
import { currentChainId, type Eip1193Provider } from "./eip1193";
import { readRpc, type RpcReadOptions } from "./read-rpc";

export type NativeV3Balances = Readonly<{ nativeBalance: bigint; tokenBalance: bigint }>;

/** Display balances only. Quote admission and pre-send checks remain separate. */
export async function readNativeV3Balances(provider: Eip1193Provider, account: Address,
  guard: () => void, options: RpcReadOptions = {}, trade: TradeConfig = ELYSIUM_TRADE): Promise<NativeV3Balances> {
  guard();
  if (await currentChainId(provider, options) !== ELYSIUM_TRADE.chainId) throw new Error("ELYSIUM_CHAIN_MISMATCH");
  guard();
  const head = await readRpc(provider, { method: "eth_getBlockByNumber", params: ["latest", false] }, options) as
    { number?: string; hash?: string; timestamp?: string } | null;
  guard();
  if (!head?.number || !/^0x[0-9a-f]+$/i.test(head.number) || BigInt(head.number) < trade.deploymentBlock ||
    !head.hash || !/^0x[0-9a-f]{64}$/i.test(head.hash) || !head.timestamp || !/^0x[0-9a-f]+$/i.test(head.timestamp))
    throw new Error("ELYSIUM_HEAD_INVALID");
  const age = BigInt(Math.floor(Date.now() / 1000)) - BigInt(head.timestamp);
  if (age < -30n || age > 120n) throw new Error("ELYSIUM_HEAD_STALE");
  const [native, token] = await Promise.all([
    readRpc(provider, { method: "eth_getBalance", params: [account, head.number] }, options),
    readRpc(provider, { method: "eth_call", params: [{ to: trade.token,
      data: encodeFunctionData({ abi: erc20Abi, functionName: "balanceOf", args: [account] }) }, head.number] }, options),
  ]);
  guard();
  if (typeof native !== "string" || !/^0x[0-9a-f]+$/i.test(native) || BigInt(native) >= 1n << 256n || typeof token !== "string")
    throw new Error("INVALID_BALANCE_READ");
  const tokenBalance = decodeFunctionResult({ abi: erc20Abi, functionName: "balanceOf", data: token as Hex });
  const [confirmed, chain] = await Promise.all([
    readRpc(provider, { method: "eth_getBlockByNumber", params: [head.number, false] }, options) as Promise<{ hash?: string } | null>,
    currentChainId(provider, options),
  ]);
  guard();
  if (chain !== ELYSIUM_TRADE.chainId) throw new Error("ELYSIUM_CHAIN_MISMATCH");
  if (!sameTradeAddress(confirmed?.hash, head.hash)) throw new Error("BALANCE_READ_REORG");
  return { nativeBalance: BigInt(native), tokenBalance };
}
